import { Slideshow } from '@theme/slideshow';

const originalSelect = Slideshow.prototype.select;

/** @type {WeakMap<Slideshow, { scroller: HTMLElement, leading: HTMLElement[], trailing: HTMLElement[] }>} */
const loops = new WeakMap();

/**
 * @param {number | string | { id: string }} input
 * @returns {number | undefined}
 */
function parseIndex(input) {
  if (typeof input === 'number') return input;
  if (typeof input === 'string') return parseInt(input, 10);
  return undefined;
}

/**
 * @param {Element} slide
 * @returns {HTMLElement}
 */
function createClone(slide) {
  const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
  clone.setAttribute('aria-hidden', 'true');
  clone.setAttribute('inert', '');
  clone.setAttribute('data-loop-clone', '');
  clone.removeAttribute('ref');
  clone.removeAttribute('slide-id');
  clone.removeAttribute('data-shopify-editor-block');
  for (const element of clone.querySelectorAll('[ref], [data-shopify-editor-block]')) {
    element.removeAttribute('ref');
    element.removeAttribute('data-shopify-editor-block');
  }
  return clone;
}

/**
 * @param {HTMLElement} scroller
 * @returns {Promise<void>}
 */
function waitForScrollStop(scroller) {
  return new Promise((resolve) => {
    const startTime = performance.now();
    let previous = scroller.scrollLeft;
    let moved = false;
    let stableFrames = 0;

    const tick = () => {
      const position = scroller.scrollLeft;

      if (Math.abs(position - previous) > 0.5) {
        moved = true;
        stableFrames = 0;
      } else if (moved) {
        stableFrames += 1;
      }
      previous = position;

      if ((moved && stableFrames >= 4) || performance.now() - startTime > 1500) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  });
}

/**
 * @param {Slideshow} component
 */
function teleportIfInCloneZone(component) {
  const loop = loops.get(component);
  const slides = component.slides;
  if (!loop || !slides?.length || component.hasAttribute('data-wrapping')) return;

  const { scroller, leading, trailing } = loop;
  const firstSlide = slides[0];
  const firstTrailing = trailing[0];
  if (!firstSlide || !firstTrailing) return;

  const scrollPadding = parseFloat(getComputedStyle(scroller).scrollPaddingInlineStart) || 0;
  const realStart = firstSlide.offsetLeft - scrollPadding;
  const realSpan = firstTrailing.offsetLeft - firstSlide.offsetLeft;
  const position = scroller.scrollLeft;

  if (position >= realStart + realSpan - 1) {
    scroller.scrollTo({ left: position - realSpan, behavior: 'instant' });
  } else if (position < realStart - 1 && leading.length > 0) {
    scroller.scrollTo({ left: position + realSpan, behavior: 'instant' });
  }
}

/**
 * @param {Slideshow} component
 */
function setup(component) {
  if (loops.has(component)) return;
  if (window.Shopify?.designMode) return;
  if (!component.hasAttribute('infinite')) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const scroller = component.refs.scroller;
  const slides = component.slides;
  if (!(scroller instanceof HTMLElement) || !slides || slides.length < 2) return;

  const cloneCount = Math.min(slides.length, 5);
  const leading = slides.slice(-cloneCount).map(createClone);
  const trailing = slides.slice(0, cloneCount).map(createClone);

  scroller.append(...trailing);
  scroller.prepend(...leading);

  const firstSlide = slides[0];
  if (firstSlide) {
    const scrollPadding = parseFloat(getComputedStyle(scroller).scrollPaddingInlineStart) || 0;
    scroller.scrollTo({ left: firstSlide.offsetLeft - scrollPadding, behavior: 'instant' });
  }

  loops.set(component, { scroller, leading, trailing });

  /** @type {ReturnType<typeof setTimeout>} */
  let settleTimer;
  scroller.addEventListener(
    'scroll',
    () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => teleportIfInCloneZone(component), 120);
    },
    { passive: true }
  );
}

/**
 * @this {Slideshow}
 * @param {number | string | { id: string }} input
 * @param {Event} [event]
 * @param {{ animate?: boolean }} [options]
 */
Slideshow.prototype.select = async function (input, event, options = {}) {
  const loop = loops.get(this);
  const { slides } = this;
  const requestedIndex = parseIndex(input);
  const lastIndex = (slides?.length ?? 0) - 1;
  const wrapsForward = requestedIndex !== undefined && requestedIndex > lastIndex;
  const wrapsBackward = requestedIndex !== undefined && requestedIndex < 0;

  if (
    !loop ||
    !(wrapsForward || wrapsBackward) ||
    options.animate === false ||
    this.hasAttribute('data-wrapping') ||
    !slides?.length
  ) {
    return originalSelect.call(this, input, event, options);
  }

  event?.preventDefault();
  this.setAttribute('data-wrapping', '');

  try {
    const target = wrapsForward ? loop.trailing[0] : loop.leading[loop.leading.length - 1];
    if (target) {
      const scrollPadding = parseFloat(getComputedStyle(loop.scroller).scrollPaddingInlineStart) || 0;
      loop.scroller.scrollTo({ left: target.offsetLeft - scrollPadding, behavior: 'smooth' });
    }
    await waitForScrollStop(loop.scroller);
    await originalSelect.call(this, wrapsForward ? 0 : lastIndex, event, { ...options, animate: false });
  } finally {
    this.removeAttribute('data-wrapping');
  }
};

const mobileMedia = window.matchMedia('(max-width: 1023px)');

/**
 * @param {'previous' | 'next'} action
 * @returns {string}
 */
function arrowMarkup(action) {
  const label = action === 'previous' ? 'Previous slide' : 'Next slide';
  const flip = action === 'previous' ? ' flip-x' : '';
  return `<button class="slideshow-control slideshow-control--${action} slideshow-control--style-arrow button button-unstyled button-unstyled--transparent${flip}" aria-label="${label}" on:click="/${action}" ref="${action}"><span class="svg-wrapper icon-arrow"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" vector-effect="non-scaling-stroke" d="M4.25 10h11.5m0 0-4-4m4 4-4 4"/></svg></span></button>`;
}

/**
 * @param {number} count
 * @returns {HTMLElement}
 */
function buildControls(count) {
  const controls = document.createElement('slideshow-controls');
  controls.setAttribute('ref', 'slideshowControls');
  controls.setAttribute('pagination-position', 'center');
  controls.setAttribute('scroll-mode', 'horizontal');
  controls.className = 'group-carousel__controls';

  let dots = '';
  for (let index = 0; index < count; index += 1) {
    dots += `<li><button class="slideshow-control button button-unstyled" aria-label="Slide ${index + 1} of ${count}" on:click="/select/${index}" ref="dots[]"${index === 0 ? ' aria-selected="true"' : ''}>${index + 1}</button></li>`;
  }

  controls.innerHTML = `<div class="slideshow-controls__arrows">${arrowMarkup('previous')}</div><ol class="slideshow-controls__dots">${dots}</ol><div class="slideshow-controls__arrows">${arrowMarkup('next')}</div>`;
  return controls;
}

/**
 * @param {HTMLElement} content
 * @returns {HTMLElement | null}
 */
function buildGroupCarousel(content) {
  const children = [...content.children];
  if (children.length < 2) return null;

  const component = document.createElement('slideshow-component');
  component.className = 'group-carousel';
  component.setAttribute('initial-slide', '0');
  component.setAttribute('infinite', '');
  component.setAttribute('data-smooth-wrap', '');

  const container = document.createElement('slideshow-container');
  container.setAttribute('ref', 'slideshowContainer');

  const scroller = document.createElement('slideshow-slides');
  scroller.setAttribute('ref', 'scroller');
  scroller.setAttribute('tabindex', '-1');

  children.forEach((child, index) => {
    const slide = document.createElement('slideshow-slide');
    slide.setAttribute('ref', 'slides[]');
    slide.setAttribute('data-index', String(index));
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    slide.append(child);
    scroller.append(slide);
  });

  container.append(scroller);
  component.append(container, buildControls(children.length));
  content.append(component);

  if (component instanceof Slideshow) setup(component);
  return component;
}

/**
 * @param {HTMLElement} content
 * @param {HTMLElement} component
 */
function destroyGroupCarousel(content, component) {
  for (const slide of component.querySelectorAll('slideshow-slide[data-index]:not([data-loop-clone])')) {
    const child = slide.firstElementChild;
    if (child) content.append(child);
  }
  component.remove();
}

/**
 * @param {HTMLElement} content
 */
function initGroupCarousel(content) {
  if (content.dataset.carouselReady) return;
  content.dataset.carouselReady = 'true';

  /** @type {HTMLElement | null} */
  let component = null;

  const apply = () => {
    if (mobileMedia.matches && !component) {
      component = buildGroupCarousel(content);
    } else if (!mobileMedia.matches && component) {
      destroyGroupCarousel(content, component);
      component = null;
    }
  };

  mobileMedia.addEventListener('change', apply);
  apply();
}

/**
 * @param {ParentNode} root
 */
function init(root) {
  for (const component of root.querySelectorAll('slideshow-component[data-smooth-wrap]')) {
    if (component instanceof Slideshow) setup(component);
  }
  for (const content of root.querySelectorAll('[data-mobile-carousel]')) {
    if (content instanceof HTMLElement) initGroupCarousel(content);
  }
}

customElements.whenDefined('slideshow-component').then(() => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) init(event.target);
});
